import { ConnectButton } from '@rainbow-me/rainbowkit';
import { ReactNode } from 'react';
import { useConnection } from 'wagmi';

import { cn } from '@/app/shared/lib/cn';
import { Button } from '@/app/shared/ui/Button';

export interface PortfolioStat {
  label: string;
  value: string;
}

export interface PortfolioPageContainerProps {
  stats?: PortfolioStat[];
  topSection?: ReactNode;
  mainSection?: ReactNode;
}

const StripeRow = () => (
  <>
    <div className='border-decoration-stroke hidden h-2.5 border-b min-[1300px]:block min-[1300px]:border-r' />
    <div className='border-decoration-stroke hidden h-2.5 border-b min-[1300px]:block' />
    <div className='border-decoration-stroke hidden h-2.5 border-b min-[1300px]:block min-[1300px]:border-l' />
  </>
);

const PortfolioPageContainer = ({ stats, topSection, mainSection }: PortfolioPageContainerProps) => {
  const { isConnected } = useConnection();
  const visibleStats = stats?.map((item) => ({
    ...item,
    value: isConnected ? item.value : '-',
  }));

  return (
    <div className='border-decoration-stroke flex min-h-[calc(100vh-230px)] flex-col overflow-x-clip md:min-h-[calc(100vh-138px)] xl:mx-[23px] xl:border-x'>
      <div className='grid min-h-0 flex-1 grid-cols-1 grid-rows-[auto_auto_1fr] min-[1300px]:grid-rows-[auto_auto_auto_auto_1fr] xl:grid-cols-[1fr_minmax(0,1244px)_1fr]'>
        <div className='border-decoration-stroke hidden h-[260px] border-b xl:block min-[1300px]:border-r' />
        <div className='border-decoration-stroke h-[260px] border-b'>{topSection}</div>
        <div className='border-decoration-stroke hidden h-[260px] border-b xl:block min-[1300px]:border-l' />

        <div className='border-decoration-stroke hidden h-[100px] border-b xl:block min-[1300px]:border-r'>
          <div
            className='hidden h-full w-full opacity-30 min-[1300px]:block'
            style={{ background: 'linear-gradient(270deg, var(--decoration-stroke) 0%, var(--background) 100%)' }}
          />
        </div>
        <div className='border-decoration-stroke grid h-auto grid-cols-2 border-b min-[960px]:h-[100px] min-[960px]:grid-cols-4'>
          {visibleStats?.map((item, index) => (
            <div
              key={item.label}
              className={cn(
                'border-decoration-stroke flex flex-col justify-between gap-1 px-5 py-[18px] min-[1300px]:px-10',
                index % 2 === 0 && 'border-r',
                index < 2 && 'border-b min-[960px]:border-b-0',
                index !== (visibleStats?.length ?? 0) - 1 && 'min-[960px]:border-r',
              )}
            >
              <p className='text-base leading-6 font-normal text-gray-400'>{item.label}</p>
              <p className='text-2xl leading-8 font-normal text-gray-200'>
                {item.value.startsWith('$') ? (
                  <>
                    <span className='text-gray-500'>$</span>
                    {item.value.slice(1)}
                  </>
                ) : (
                  item.value
                )}
              </p>
            </div>
          ))}
        </div>
        <div className='border-decoration-stroke hidden h-[100px] border-b xl:block min-[1300px]:border-l'>
          <div
            className='hidden h-full w-full opacity-30 min-[1300px]:block'
            style={{ background: 'linear-gradient(90deg, var(--decoration-stroke) 0%, var(--background) 100%)' }}
          />
        </div>

        <StripeRow />
        <StripeRow />

        <div className='border-decoration-stroke hidden xl:block min-[1300px]:border-r' />
        <div
          className={cn(
            'flex min-h-0 w-full min-w-0 flex-col overflow-visible px-5 py-10 min-[1300px]:px-10',
            isConnected ? 'gap-10' : 'flex-1 items-center justify-center',
          )}
        >
          {isConnected ? (
            mainSection
          ) : (
            <div className='flex flex-col items-center gap-4'>
              <h2 className='text-xl font-medium text-foreground'>Connect your wallet first</h2>
              <p className='max-w-sm text-center text-sm leading-6 font-normal text-gray-400'>
                To view Portfolio, you need to connect your wallet first.
              </p>
              <ConnectButton.Custom>
                {({ openConnectModal }) => (
                  <Button
                    variant='outline'
                    onClick={openConnectModal}
                    className='rounded-md px-5 py-3'
                  >
                    Connect Wallet
                  </Button>
                )}
              </ConnectButton.Custom>
            </div>
          )}
        </div>
        <div className='border-decoration-stroke hidden xl:block min-[1300px]:border-l' />
      </div>
    </div>
  );
};

export default PortfolioPageContainer;
